import { logger } from '@/lib/logger';

/**
 * Storage key format for multi-org flag.
 * This flag indicates whether a user has access to multiple organizations.
 */
const getStorageKey = (userId: string): string => {
  return `ahoy-has-multiple-orgs-${userId}`;
};

/**
 * Check if a user has the multi-org flag set in localStorage.
 *
 * @param userId - The user's ID
 * @returns true if the user has multiple orgs, false otherwise
 */
export const hasMultiOrgFlag = (userId: string | undefined): boolean => {
  if (!userId) {
    return false;
  }

  try {
    const storageKey = getStorageKey(userId);
    return localStorage.getItem(storageKey) === 'true';
  } catch (error) {
    logger.warn('Failed to read multi-org flag from localStorage', {
      userId,
      error: String(error),
      component: 'org-context',
    });
    return false;
  }
};

/**
 * Update the multi-org flag for a user in localStorage.
 * Sets the flag if the user has multiple orgs, removes it if they have one or zero.
 *
 * @param userId - The user's ID
 * @param hasMultipleOrgs - Whether the user has multiple organizations
 */
export const updateMultiOrgFlag = (
  userId: string,
  hasMultipleOrgs: boolean
): void => {
  try {
    const storageKey = getStorageKey(userId);
    if (hasMultipleOrgs) {
      localStorage.setItem(storageKey, 'true');
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (error) {
    logger.warn('Failed to persist multi-org flag to localStorage', {
      userId,
      error: String(error),
      component: 'org-context',
    });
  }
};

/**
 * Clear all multi-org flags from localStorage.
 * Used during logout to remove user-specific flags while preserving device preferences.
 */
export const clearAllMultiOrgFlags = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('ahoy-has-multiple-orgs-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    logger.warn('Failed to clear multi-org flags from localStorage', {
      error: String(error),
      component: 'org-context',
    });
  }
};
